import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/images/logo.png";
import type { NavLink } from "../../shared/types/navigation";
import type { MouseEvent } from "react";

type SiteFooterProps = {
  navLinks?: NavLink[];
};

function SiteFooter({ navLinks = [] }: SiteFooterProps) {
  const location = useLocation();
  const isProfilePage = location.pathname === "/profile";
  const hoverBorderClass = isProfilePage
    ? "hover:border-blue-300"
    : "hover:border-red-300";
  const hoverTextClass = isProfilePage
    ? "group-hover:text-blue-600"
    : "group-hover:text-red-600";

  const quickLinks =
    navLinks.length > 0
      ? navLinks
      : [
          { label: "サービス", href: "#services" },
          { label: "強み", href: "#strengths" },
          { label: "流れ", href: "#process" },
          { label: "実績", href: "#works" },
          { label: "料金プラン", href: "#pricing" },
          { label: "お問い合わせ", href: "#contact" },
        ];

  const getHeaderOffset = () =>
    document.querySelector("header")?.getBoundingClientRect().height ?? 0;

  const handleQuickLinkClick = (
    e: MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    if (!href.startsWith("#")) return;
    e.preventDefault();
    window.dispatchEvent(new CustomEvent("nav-scroll", { detail: href }));
    const target = document.querySelector(href);
    if (!target) return;

    const targetTop = (target as HTMLElement).getBoundingClientRect().top;
    const scrollTarget = targetTop + window.scrollY - getHeaderOffset();

    window.scrollTo({
      top: scrollTarget,
      behavior: "smooth",
    });
  };

  return (
    <footer className="bg-white px-6 pb-8 pt-20 md:px-12 lg:px-24">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 grid grid-cols-1 gap-12 md:grid-cols-2">
          <div className="md:col-span-1">
            <div className="mb-4">
              <Link to="/" aria-label="cocoIT ホーム">
                <img src={logo} alt="cocoIT Logo" className="h-16 w-auto" />
              </Link>
            </div>
            <p className="text-sm leading-relaxed text-text-muted">
              アプリ開発、Web制作、保守運用を通じて、
              <br />
              お客様のビジネス成長を技術で支援します。
            </p>
          </div>
          <div className="flex items-center md:col-span-1 md:justify-end">
            <ul className="flex flex-wrap gap-4 md:gap-6">
              {quickLinks.map((link, index) => {
                const isLast = index === quickLinks.length - 1;
                return (
                  <li
                    key={link.href}
                    className={`group flex items-center ${
                      isLast
                        ? ""
                        : `border-r border-gray-300 pr-4 md:pr-6 ${hoverBorderClass}`
                    }`}
                  >
                    <a
                      href={link.href}
                      target={link.external ? "_blank" : undefined}
                      rel={link.external ? "noreferrer noopener" : undefined}
                      className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-text-muted transition-colors ${hoverTextClass}`}
                      onClick={
                        link.href.startsWith("#")
                          ? (e) => handleQuickLinkClick(e, link.href)
                          : undefined
                      }
                    >
                      <span>{link.label}</span>
                      {link.icon && link.href === "/profile" ? (
                        <i
                          className={`${link.icon} text-[1em] text-current`}
                        ></i>
                      ) : null}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
        <div className="via-text-darker mb-6 h-px bg-gradient-to-r from-transparent to-transparent"></div>
        <div className="text-text-darker flex flex-col items-center justify-center gap-4 text-center text-sm md:flex-row">
          <p>&copy; 2017 cocoIT. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

export default SiteFooter;
